import type { ChronologicCommunication, TrackerEmail, CommunicationType } from '../types/tracker.types';
import type { ActivityItemViewModel, ActivityAttachment, UrgencyLevel } from '../components/ActivityItem';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Parse a date string to Date object safely
 */
const parseDate = (dateStr: string | null | undefined): Date => {
  if (!dateStr) return new Date();
  try {
    const parsed = parseISO(dateStr);
    return isValid(parsed) ? parsed : new Date();
  } catch {
    return new Date();
  }
};

/**
 * Format a date for meta display
 */
const formatMetaDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const date = parseDate(dateStr);
  return format(date, 'dd/MM HH:mm', { locale: fr });
};

/**
 * Truncate text to a maximum length
 */
const truncate = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Clean HTML content to plain text
 */
const htmlToText = (html: string | null | undefined): string => {
  if (!html) return '';
  // Remove HTML tags and decode entities
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Léa is the AI assistant — display her branded name instead of her DB name
 */
const LEA_USER_ID = 'a1ea0000-0000-4000-b000-0000000001ea';
const LEA_DISPLAY_NAME = 'Léa Assistante Digitale';

const applyLeaDisplayName = (name: string | null | undefined, userId?: string | null): string | undefined => {
  if (!name) return undefined;
  if (userId === LEA_USER_ID) return LEA_DISPLAY_NAME;
  if (/^l[ée]a\s+chatelain$/i.test(name.trim())) return LEA_DISPLAY_NAME;
  return name;
};

/**
 * Get the display name for the user who marked an item as done
 */
const getDoneByDisplayName = (communication: ChronologicCommunication): string | undefined => {
  if (communication.done_by_first_name || communication.done_by_last_name) {
    const name = `${communication.done_by_first_name || ''} ${communication.done_by_last_name || ''}`.trim();
    return applyLeaDisplayName(name, communication.done_by);
  }
  return undefined;
};

/**
 * Get the display name for an assigned user
 */
const getAssignedDisplayName = (communication: ChronologicCommunication): string | undefined => {
  if (communication.assigned_user_first_name || communication.assigned_user_last_name) {
    const name = `${communication.assigned_user_first_name || ''} ${communication.assigned_user_last_name || ''}`.trim();
    return applyLeaDisplayName(name, communication.assigned_to);
  }
  if (communication.assigned_user_fm_name) {
    return communication.assigned_user_fm_name;
  }
  return undefined;
};

/**
 * Adapter for post-it (com_post_it) communications
 */
export const adaptPostIt = (comm: ChronologicCommunication): ActivityItemViewModel => {
  const hasTodo = comm.todo && comm.todo.trim().length > 0;
  const metaParts: string[] = [];

  if (hasTodo) {
    metaParts.push('A faire');
  }
  if (comm.seen_by) {
    metaParts.push(`Vu par: ${comm.seen_by}`);
  }

  return {
    type: 'com_post_it',
    id: comm.id,
    title: applyLeaDisplayName(comm.author, comm.author_id) || 'Inconnu', // Type badge shown below icon
    meta: metaParts.join(' • ') || undefined,
    snippet: truncate(comm.content, 150),
    fullContent: comm.content || undefined,
    timestamp: parseDate(comm.sort_date || comm.event_date),
    urgency: comm.urgency as UrgencyLevel,
    isDone: comm.is_done || false,
    doneBy: getDoneByDisplayName(comm) || comm.done_by || undefined,
    doneAt: comm.done_at ? parseDate(comm.done_at) : undefined,
    doneComment: comm.done_comment || undefined,
    author: applyLeaDisplayName(comm.author, comm.author_id) || undefined,
    authorId: comm.author_id || undefined,
    assignedTo: getAssignedDisplayName(comm),
    assignedToId: comm.assigned_to || undefined,
    rawData: comm,
  };
};

/**
 * Adapter for received emails
 */
export const adaptEmailReceived = (email: TrackerEmail): ActivityItemViewModel => {
  const metaParts: string[] = [];

  if (email.subject) {
    metaParts.push(`Objet: ${truncate(email.subject, 50)}`);
  }

  // Use email_time (unified field) or fall back to received/sent time
  const emailDate = email.email_time || email.created_at;

  // Get snippet from body preview or full content
  const snippet = email.body_preview || htmlToText(email.full_content_html) || email.full_content || '';

  // Convert attachments
  const attachments: ActivityAttachment[] = (email.attachments || []).map(att => ({
    id: att.id,
    name: att.name,
    contentType: att.contentType || att.content_type || 'application/octet-stream',
    size: att.size,
    isInline: att.isInline || att.is_inline,
  }));

  return {
    type: 'email',
    id: email.id,
    title: email.sender_name || email.sender_address || 'Email', // Just sender name
    meta: metaParts.join(' • ') || undefined,
    snippet: truncate(snippet, 200),
    fullContent: email.full_content || undefined,
    htmlContent: email.full_content_html || undefined,
    timestamp: parseDate(emailDate),
    urgency: email.urgency as UrgencyLevel,
    isDone: email.is_done || false,
    doneBy: applyLeaDisplayName(email.done_by || undefined) || undefined,
    doneAt: email.done_at ? parseDate(email.done_at) : undefined,
    isUnread: !email.is_read,
    assignedTo: email.assigned_user_first_name
      ? applyLeaDisplayName(`${email.assigned_user_first_name} ${email.assigned_user_last_name || ''}`.trim(), email.assigned_to)
      : undefined,
    assignedToId: email.assigned_to || undefined,
    direction: 'inbound',
    attachments: attachments.length > 0 ? attachments : undefined,
    rawData: email,
  };
};

/**
 * Adapter for sent emails
 */
export const adaptEmailSent = (email: TrackerEmail): ActivityItemViewModel => {
  const metaParts: string[] = [];

  // Get recipients
  const toRecipients = email.recipients_to?.map(r => r.name || r.address).join(', ');
  if (toRecipients) {
    metaParts.push(`À: ${truncate(toRecipients, 40)}`);
  } else if (email.receiver_address) {
    metaParts.push(`À: ${email.receiver_address}`);
  }

  const emailDate = email.email_time || email.created_at;
  const snippet = email.body_preview || htmlToText(email.full_content_html) || email.full_content || '';

  const attachments: ActivityAttachment[] = (email.attachments || []).map(att => ({
    id: att.id,
    name: att.name,
    contentType: att.contentType || att.content_type || 'application/octet-stream',
    size: att.size,
    isInline: att.isInline || att.is_inline,
  }));

  return {
    type: 'email',
    id: email.id,
    title: truncate(email.subject || 'Sans objet', 50), // Just subject for sent emails
    meta: metaParts.join(' • ') || undefined,
    snippet: truncate(snippet, 200),
    fullContent: email.full_content || undefined,
    htmlContent: email.full_content_html || undefined,
    timestamp: parseDate(emailDate),
    urgency: email.urgency as UrgencyLevel,
    isDone: email.is_done || false,
    doneBy: applyLeaDisplayName(email.done_by || undefined) || undefined,
    doneAt: email.done_at ? parseDate(email.done_at) : undefined,
    assignedTo: email.assigned_user_first_name
      ? applyLeaDisplayName(`${email.assigned_user_first_name} ${email.assigned_user_last_name || ''}`.trim(), email.assigned_to)
      : undefined,
    assignedToId: email.assigned_to || undefined,
    direction: 'outbound',
    attachments: attachments.length > 0 ? attachments : undefined,
    rawData: email,
  };
};

/**
 * Adapter for outbound SMS
 */
export const adaptSmsOutbox = (comm: ChronologicCommunication): ActivityItemViewModel => {
  // Get recipient phone from the recipient field (set by backend query)
  const recipientPhone = comm.recipient?.trim() || '';

  return {
    type: 'sms_outbox',
    id: comm.id,
    title: 'SMS', // Type shown by badge, recipient shown separately
    meta: undefined,
    snippet: truncate(comm.content, 150),
    fullContent: comm.content || undefined,
    timestamp: parseDate(comm.sort_date || comm.event_date),
    urgency: comm.urgency as UrgencyLevel,
    isDone: comm.is_done || false,
    doneBy: getDoneByDisplayName(comm) || comm.done_by || undefined,
    doneAt: comm.done_at ? parseDate(comm.done_at) : undefined,
    doneComment: comm.done_comment || undefined,
    author: applyLeaDisplayName(comm.author, comm.author_id) || undefined,
    assignedTo: getAssignedDisplayName(comm),
    assignedToId: comm.assigned_to || undefined,
    direction: 'outbound',
    recipient: recipientPhone || undefined,
    rawData: comm,
  };
};

/**
 * Adapter for inbound SMS
 */
export const adaptSmsInbox = (comm: ChronologicCommunication): ActivityItemViewModel => {
  // Sender info: author contains the sender name (from contacts lookup) or phone
  const senderName = comm.author?.trim();
  // Sender phone: recipient contains the sender's phone number for inbox
  const senderPhone = comm.recipient?.trim();

  return {
    type: 'sms_inbox',
    id: comm.id,
    title: 'SMS', // Type shown by badge, sender shown separately
    meta: undefined,
    snippet: truncate(comm.content, 150),
    fullContent: comm.content || undefined,
    timestamp: parseDate(comm.sort_date || comm.event_date),
    urgency: comm.urgency as UrgencyLevel,
    isDone: comm.is_done || false,
    doneBy: getDoneByDisplayName(comm) || comm.done_by || undefined,
    doneAt: comm.done_at ? parseDate(comm.done_at) : undefined,
    doneComment: comm.done_comment || undefined,
    isUnread: !comm.is_read,
    author: senderName || senderPhone || undefined,
    assignedTo: getAssignedDisplayName(comm),
    assignedToId: comm.assigned_to || undefined,
    direction: 'inbound',
    recipient: senderPhone || undefined, // For inbox, this is the sender's phone
    rawData: comm,
  };
};

/**
 * Adapter for status updates
 */
export const adaptStatusUpdate = (comm: ChronologicCommunication): ActivityItemViewModel => {
  const metaParts: string[] = [];

  if (comm.user_initials) {
    metaParts.push(`par ${comm.user_initials}`);
  }
  // Could add "de X à Y" if we had the previous status

  return {
    type: 'status_update',
    id: comm.id,
    title: comm.status || 'Statut inconnu', // Type badge shown below icon
    meta: metaParts.join(' • ') || undefined,
    snippet: undefined, // Status updates don't have body content
    timestamp: parseDate(comm.sort_date || comm.event_date),
    author: comm.user_initials || undefined,
    rawData: comm,
  };
};

/**
 * Adapter for tracker communications (tracker_com)
 */
export const adaptTrackerCom = (comm: ChronologicCommunication): ActivityItemViewModel => {
  const metaParts: string[] = [];

  if (getAssignedDisplayName(comm)) {
    metaParts.push(`Assigné à: ${getAssignedDisplayName(comm)}`);
  }
  if (comm.metadata_comment) {
    metaParts.push(comm.metadata_comment);
  }

  // Léa-generated coms: who accepted (validated) the underlying action plan.
  const planValidatedByName = [comm.plan_validated_by_first_name, comm.plan_validated_by_last_name]
    .filter(Boolean).join(' ').trim() || undefined;

  // Check if the message was edited (updated_at differs from created_date by more than 1 second)
  let isEdited = false;
  let editedAt: Date | undefined;
  if (comm.created_date && comm.updated_at) {
    const createdDate = parseDate(comm.created_date);
    const updatedDate = parseDate(comm.updated_at);
    // Consider it edited if updated_at is more than 1 second after created_date
    if (updatedDate.getTime() - createdDate.getTime() > 1000) {
      isEdited = true;
      editedAt = updatedDate;
    }
  }

  return {
    type: 'tracker_com',
    id: comm.id,
    title: applyLeaDisplayName(comm.author, comm.author_id) || 'Inconnu', // Type badge shown below icon
    meta: metaParts.join(' • ') || undefined,
    snippet: truncate(comm.content, 150),
    fullContent: comm.content || undefined,
    timestamp: parseDate(comm.sort_date || comm.event_date),
    urgency: comm.urgency as UrgencyLevel,
    isDone: comm.is_done || false,
    doneBy: getDoneByDisplayName(comm) || comm.done_by || undefined,
    doneAt: comm.done_at ? parseDate(comm.done_at) : undefined,
    doneComment: comm.done_comment || undefined,
    author: applyLeaDisplayName(comm.author, comm.author_id) || undefined,
    authorId: comm.author_id || undefined,
    assignedTo: getAssignedDisplayName(comm),
    assignedToId: comm.assigned_to || undefined,
    planValidatedByName,
    isEdited,
    editedAt,
    rawData: comm,
  };
};

/**
 * Adapter for internal communications (com_internes)
 */
export const adaptComInternes = (comm: ChronologicCommunication): ActivityItemViewModel => {
  const metaParts: string[] = [];

  if (comm.recipient_display_name || comm.recipient) {
    metaParts.push(`À: ${comm.recipient_display_name || comm.recipient}`);
  }
  if (comm.opened_at) {
    metaParts.push(`Ouvert: ${formatMetaDate(comm.opened_at)}`);
  }

  return {
    type: 'com_internes',
    id: comm.id,
    title: applyLeaDisplayName(comm.author, comm.author_id) || 'Inconnu', // Type badge shown below icon
    meta: metaParts.join(' • ') || undefined,
    snippet: truncate(comm.content, 150),
    fullContent: comm.content || undefined,
    timestamp: parseDate(comm.sort_date || comm.event_date),
    urgency: comm.urgency as UrgencyLevel,
    isDone: comm.is_done || false,
    doneBy: getDoneByDisplayName(comm) || comm.done_by || undefined,
    doneAt: comm.done_at ? parseDate(comm.done_at) : undefined,
    doneComment: comm.done_comment || undefined,
    isUnread: !comm.is_read,
    author: applyLeaDisplayName(comm.author, comm.author_id) || undefined,
    authorId: comm.author_id || undefined,
    assignedTo: getAssignedDisplayName(comm),
    assignedToId: comm.assigned_to || undefined,
    rawData: comm,
  };
};

/**
 * Adapter for pre-send communications (com_avant_envoyer)
 */
export const adaptComAvantEnvoyer = (comm: ChronologicCommunication): ActivityItemViewModel => {
  return {
    type: 'com_avant_envoyer',
    id: comm.id,
    title: applyLeaDisplayName(comm.author, comm.author_id) || 'Inconnu', // Type badge shown below icon
    meta: undefined,
    snippet: truncate(comm.content, 150),
    fullContent: comm.content || undefined,
    timestamp: parseDate(comm.sort_date || comm.event_date),
    author: applyLeaDisplayName(comm.author, comm.author_id) || undefined,
    authorId: comm.author_id || undefined,
    rawData: comm,
  };
};

/**
 * Adapter for VoIP call recordings. Renders the inline audio player
 * via `recordingUrl` + a short title summarizing caller + duration.
 */
export const adaptVoipCall = (comm: ChronologicCommunication): ActivityItemViewModel => {
  const callerNumber = comm.voip_caller_number ?? comm.author ?? null;
  const durationMs = typeof comm.duration_ms === 'number' ? comm.duration_ms : null;
  const direction = comm.status === 'inbound' ? 'inbound' : comm.status === 'outbound' ? 'outbound' : undefined;
  const directionLabel =
    direction === 'inbound' ? 'Appel entrant' : direction === 'outbound' ? 'Appel sortant' : 'Appel';

  const contactName = comm.voip_contact_name?.trim() || null;
  const contactRoles = comm.voip_contact_roles?.trim() || null;
  const agentName =
    [comm.voip_agent_first_name, comm.voip_agent_last_name]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    comm.voip_agent_fm_name?.trim() ||
    null;

  // Prefer the contact name in the title; fall back to the raw number.
  const headLabel = contactName || callerNumber || null;
  const title = headLabel ? `${directionLabel} — ${headLabel}` : directionLabel;

  return {
    type: 'voip_call',
    id: comm.id,
    title,
    // Snippet intentionally empty — the summary is rendered in its
    // own dedicated block inside the voip card, so showing it twice
    // (snippet row + summary box) would be redundant.
    snippet: undefined,
    timestamp: parseDate(comm.sort_date || comm.event_date),
    direction,
    recipient: callerNumber || undefined,
    recordingUrl: comm.recording_url || undefined,
    durationMs: durationMs ?? undefined,
    voipContactName: contactName,
    voipContactRoles: contactRoles,
    voipAgentName: agentName,
    voipSummary: comm.voip_summary?.trim() || null,
    voipTranscription: comm.voip_transcription?.trim() || null,
    voipProcessedAt: comm.voip_processed_at || null,
    rawData: comm,
  };
};

/**
 * Main adapter function that routes to type-specific adapters
 */
export const adaptCommunication = (comm: ChronologicCommunication): ActivityItemViewModel => {
  switch (comm.type) {
    case 'com_post_it':
      return adaptPostIt(comm);
    case 'sms_outbox':
      return adaptSmsOutbox(comm);
    case 'sms_inbox':
      return adaptSmsInbox(comm);
    case 'status_update':
      return adaptStatusUpdate(comm);
    case 'tracker_com':
      return adaptTrackerCom(comm);
    case 'com_internes':
      return adaptComInternes(comm);
    case 'com_avant_envoyer':
      return adaptComAvantEnvoyer(comm);
    case 'voip_call':
      return adaptVoipCall(comm);
    default:
      // Fallback for unknown types
      return {
        type: comm.type,
        id: comm.id,
        title: `Communication — ${applyLeaDisplayName(comm.author, comm.author_id) || 'Inconnu'}`,
        snippet: truncate(comm.content, 150),
        fullContent: comm.content || undefined,
        timestamp: parseDate(comm.sort_date || comm.event_date),
        author: applyLeaDisplayName(comm.author, comm.author_id) || undefined,
        rawData: comm,
      };
  }
};

/**
 * Adapter for email (handles both received and sent)
 */
export const adaptEmail = (email: TrackerEmail): ActivityItemViewModel => {
  if (email.email_type === 'sent') {
    return adaptEmailSent(email);
  }
  return adaptEmailReceived(email);
};

/**
 * Merge and sort communications and emails into a unified timeline
 */
export const mergeAndSortActivities = (
  communications: ChronologicCommunication[],
  emails: TrackerEmail[],
  sortOrder: 'asc' | 'desc' = 'desc'
): ActivityItemViewModel[] => {
  // Adapt all communications
  const adaptedComms = communications.map(adaptCommunication);

  // Adapt all emails
  const adaptedEmails = emails.map(adaptEmail);

  // Merge and sort
  const merged = [...adaptedComms, ...adaptedEmails];

  merged.sort((a, b) => {
    const timeA = a.timestamp.getTime();
    const timeB = b.timestamp.getTime();
    return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
  });

  return merged;
};

/**
 * Group activities by day
 */
export interface ActivityDayGroup {
  date: Date;
  label: string;
  activities: ActivityItemViewModel[];
}

export const groupActivitiesByDay = (
  activities: ActivityItemViewModel[]
): ActivityDayGroup[] => {
  const groups = new Map<string, ActivityItemViewModel[]>();

  for (const activity of activities) {
    const dateKey = format(activity.timestamp, 'yyyy-MM-dd');
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(activity);
  }

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const result: ActivityDayGroup[] = [];

  for (const [dateKey, dayActivities] of groups) {
    const date = parseISO(dateKey);
    let label: string;

    if (format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
      label = "Aujourd'hui";
    } else if (format(date, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd')) {
      label = 'Hier';
    } else {
      label = format(date, 'EEEE d MMMM yyyy', { locale: fr });
      // Capitalize first letter
      label = label.charAt(0).toUpperCase() + label.slice(1);
    }

    result.push({
      date,
      label,
      activities: dayActivities,
    });
  }

  // Sort groups by date (newest first)
  result.sort((a, b) => b.date.getTime() - a.date.getTime());

  return result;
};

/**
 * Filter activities by type
 */
export const filterActivitiesByType = (
  activities: ActivityItemViewModel[],
  enabledTypes: Set<CommunicationType>
): ActivityItemViewModel[] => {
  return activities.filter(activity => enabledTypes.has(activity.type));
};

/**
 * Search activities by text
 */
export const searchActivities = (
  activities: ActivityItemViewModel[],
  searchQuery: string
): ActivityItemViewModel[] => {
  if (!searchQuery.trim()) return activities;

  const query = searchQuery.toLowerCase().trim();

  return activities.filter(activity => {
    const searchableText = [
      activity.title,
      activity.meta,
      activity.snippet,
      activity.fullContent,
      activity.author,
      activity.assignedTo,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(query);
  });
};
